import { Box, Button, Checkbox, HStack, Input, Radio, RadioGroup, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminGetSearchLogs, adminUpdateSearchLogChecked } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminSearchLog, IAdminSearchLogList } from "../../../types";
import { formatDateTimeKor } from "../../../utils";

function toNullableBoolean(value: string): boolean | null {
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
}

export default function SearchLogs() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        keyword: string;
    }

    const { register, watch, handleSubmit, getValues, formState: {errors, isValid}, setError, setValue } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [keyword, setKeyword] = useState<string | null>(null);
    const [isNoResult, setIsNoResult] = useState("all");
    const [isChecked, setIsChecked] = useState("all");

    const onSubmit = ({ keyword }: ISearchForm) => {
        setPage(1);
        setKeyword(keyword);
    }

    const onChangeIsNoResult = (value: string) => {
        setPage(1);
        setIsNoResult(value);
    }

    const onChangeIsChecked = (value: string) => {
        setPage(1);
        setIsChecked(value);
    }

    const isNoResultParam = toNullableBoolean(isNoResult);
    const isCheckedParam = toNullableBoolean(isChecked);

    /* query */

    const queryClient = useQueryClient();
    const toast = useToast();
    const searchLogQueryKey = ['adminGetSearchLogs', page, size, keyword, isNoResultParam, isCheckedParam] as const;

    const { data: searchLogListData, isLoading: isSearchLogListDataLoading } = useQuery<IAdminSearchLogList>({
        queryKey: searchLogQueryKey,
        queryFn: () => adminGetSearchLogs({ page, size, keyword, isNoResult: isNoResultParam, isChecked: isCheckedParam }),
        enabled: true,
    });

    const updateSearchLogCheckedMutation = useMutation({
        mutationFn: adminUpdateSearchLogChecked,
        onMutate: async ({ searchLogId }) => {
            await queryClient.cancelQueries({ queryKey: searchLogQueryKey });
            const previous = queryClient.getQueryData<IAdminSearchLogList>(searchLogQueryKey);
            if (previous) {
                queryClient.setQueryData<IAdminSearchLogList>(searchLogQueryKey, {
                    ...previous,
                    searchLogs: previous.searchLogs.map((log) => (
                        log.id === searchLogId ? { ...log, isChecked: !log.isChecked } : log
                    )),
                });
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(searchLogQueryKey, context.previous);
            }
            toast({
                title: "확인 여부 변경에 실패했습니다.",
                status: "error",
                position: "top",
                duration: 2000,
                isClosable: true,
            });
        },
        onSuccess: (data, { searchLogId }) => {
            if (!data || typeof data !== "object" || typeof data.isChecked !== "boolean") return;
            queryClient.setQueryData<IAdminSearchLogList>(searchLogQueryKey, (prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    searchLogs: prev.searchLogs.map((log) => (
                        log.id === searchLogId ? { ...log, ...data } : log
                    )),
                };
            });
        },
    });

    const totalPages = useMemo(() => searchLogListData?.totalPages ?? 0, [searchLogListData]);
    const currentPage = useMemo(() => searchLogListData?.currentPage ?? 0, [searchLogListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* log columns */

    const searchLogColumns = [
        { key: "index", label: "연번" },
        { key: "keyword", label: "검색 키워드" },
        { key: "searchCount", label: "검색 수" },
        { key: "resultCount", label: "최근 검색 결과 수" },
        { key: "lastSearchedAt", label: "최근 검색 시간" },
        { key: "isChecked", label: "확인 여부" },
    ]

    const renderSearchLogCell = (searchLog: IAdminSearchLog, columnKey: string, index: number) => {
        if (columnKey === "index") return String(index + 1);
        if (columnKey === "lastSearchedAt") return formatDateTimeKor(searchLog.lastSearchedAt);
        if (columnKey === "isChecked") {
            if (searchLog.resultCount !== 0) return null;
            return (
                <Checkbox
                    colorScheme="blue"
                    isChecked={searchLog.isChecked}
                    isDisabled={
                        updateSearchLogCheckedMutation.isPending &&
                        updateSearchLogCheckedMutation.variables?.searchLogId === searchLog.id
                    }
                    onChange={() => updateSearchLogCheckedMutation.mutate({ searchLogId: searchLog.id })}
                />
            );
        }
        return String(searchLog[columnKey as keyof IAdminSearchLog] ?? "-");
    }

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>검색 로그</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{searchLogListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>
                <RadioGroup value={isNoResult} onChange={onChangeIsNoResult}>
                    <HStack spacing={6} marginTop={4}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}
                            width={"88px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>무결과 여부</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">선택 안함</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>
                <RadioGroup value={isChecked} onChange={onChangeIsChecked}>
                    <HStack spacing={6}>
                        <Box
                            paddingX={3}
                            paddingY={1}
                            backgroundColor={colors.light}
                            borderRadius={10}
                            width={"88px"}
                            textAlign={"center"}
                        >
                            <Text fontSize={12} fontWeight={500} color={colors.blue}>체크 여부</Text>
                        </Box>
                        <Radio value="all" colorScheme="blue">선택 안함</Radio>
                        <Radio value="true" colorScheme="blue">TRUE</Radio>
                        <Radio value="false" colorScheme="blue">FALSE</Radio>
                    </HStack>
                </RadioGroup>
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("keyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* table */}

                {!isSearchLogListDataLoading ? (
                    <TableContainer marginTop={10}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    {searchLogColumns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {searchLogListData?.searchLogs.map((searchLog, index) => (
                                    <Tr key={searchLog.id}>
                                        {searchLogColumns.map((column, idx) => (
                                            <Td key={idx}>
                                                {renderSearchLogCell(searchLog, column.key, index)}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}